import React, { forwardRef } from 'react';
import { Link, LinkProps } from '@inertiajs/react';
import { useSmartPrefetch, useHoverPrefetch } from '@/hooks/useSmartPrefetch';

interface SmartLinkProps extends Omit<LinkProps, 'href'> {
  href: string;
  children: React.ReactNode;
  prefetchStrategy?: 'viewport' | 'hover' | 'immediate' | 'none';
  prefetchDelay?: number;
  cacheFor?: string;
  priority?: 'high' | 'low' | 'auto';
  className?: string;
  onPrefetchStart?: () => void;
  onPrefetchComplete?: () => void;
}

/**
 * Smart Link component with intelligent prefetching strategies
 * 
 * Strategies:
 * - viewport: Prefetch when link enters viewport (default)
 * - hover: Prefetch on mouse hover
 * - immediate: Prefetch immediately on mount
 * - none: No prefetching
 */
const SmartLink = forwardRef<HTMLAnchorElement, SmartLinkProps>(({
  href,
  children,
  prefetchStrategy = 'viewport',
  prefetchDelay = 100,
  cacheFor = '10s',
  priority = 'auto',
  className = '',
  onPrefetchStart,
  onPrefetchComplete,
  ...linkProps
}, ref) => {
  // Viewport-based prefetch
  const {
    elementRef: viewportRef,
    isPrefetched: isViewportPrefetched,
    isPrefetching: isViewportPrefetching,
    triggerPrefetch
  } = useSmartPrefetch(href, {
    delay: prefetchDelay,
    cacheFor,
    priority
  });

  // Hover-based prefetch
  const {
    isPrefetched: isHoverPrefetched,
    onMouseEnter,
    onMouseLeave
  } = useHoverPrefetch(href, {
    delay: prefetchDelay,
    cacheFor
  });

  // Immediate prefetch on mount
  React.useEffect(() => {
    if (prefetchStrategy === 'immediate') {
      triggerPrefetch();
    }
  }, [prefetchStrategy, triggerPrefetch]);

  // Handle prefetch callbacks
  React.useEffect(() => {
    if (isViewportPrefetched || isHoverPrefetched) {
      onPrefetchComplete?.();
    }
  }, [isViewportPrefetched, isHoverPrefetched, onPrefetchComplete]);

  React.useEffect(() => {
    if (isViewportPrefetching) {
      onPrefetchStart?.();
    }
  }, [isViewportPrefetching, onPrefetchStart]);

  // Combine refs
  const combinedRef = React.useCallback((node: HTMLAnchorElement) => {
    if (prefetchStrategy === 'viewport') {
      (viewportRef as React.MutableRefObject<HTMLAnchorElement>).current = node;
    }
    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    }
  }, [viewportRef, ref, prefetchStrategy]);

  // Determine hover handlers based on strategy
  const hoverHandlers = prefetchStrategy === 'hover' ? {
    onMouseEnter,
    onMouseLeave
  } : {};

  // Add loading indicator class if prefetching
  const finalClassName = `${className} ${
    isViewportPrefetching ? 'prefetching' : ''
  } ${
    (isViewportPrefetched || isHoverPrefetched) ? 'prefetched' : ''
  }`.trim();

  return (
    <Link
      ref={combinedRef}
      href={href}
      className={finalClassName}
      {...hoverHandlers}
      {...linkProps}
    >
      {children}
      
      {/* Optional prefetch indicator */}
      {process.env.NODE_ENV === 'development' && isViewportPrefetching && (
        <span 
          className="absolute top-0 right-0 w-2 h-2 bg-blue-500 rounded-full animate-pulse"
          title="Prefetching..."
        />
      )}
    </Link>
  );
});

SmartLink.displayName = 'SmartLink';

export default SmartLink;

/**
 * Specialized SmartLink for news articles with optimized settings
 */
export const NewsLink: React.FC<Omit<SmartLinkProps, 'prefetchStrategy' | 'cacheFor'> & {
  newsSlug: string;
  prefetchStrategy?: SmartLinkProps['prefetchStrategy'];
}> = ({ 
  newsSlug, 
  prefetchStrategy = 'viewport',
  ...props 
}) => {
  return (
    <SmartLink
      href={`/news/${newsSlug}`}
      prefetchStrategy={prefetchStrategy}
      cacheFor="30s" // News articles cached longer
      priority="high"
      {...props}
    />
  );
};

/**
 * Specialized SmartLink for category pages
 */
export const CategoryLink: React.FC<Omit<SmartLinkProps, 'prefetchStrategy' | 'cacheFor'> & {
  categorySlug: string;
}> = ({ 
  categorySlug, 
  ...props 
}) => {
  return (
    <SmartLink
      href={`/category/${categorySlug}`}
      prefetchStrategy="hover"
      cacheFor="60s" // Category pages cached even longer
      priority="low"
      {...props}
    />
  );
};

/**
 * Hook for batch prefetching related articles
 */
export const useBatchPrefetch = (newsIds: number[]) => {
  const [prefetchedCount, setPrefetchedCount] = React.useState(0);

  const prefetchBatch = React.useCallback(async (batchSize = 3) => {
    const batch = newsIds.slice(prefetchedCount, prefetchedCount + batchSize);
    
    const prefetchPromises = batch.map(async (id) => {
      try {
        // Assuming we have a way to get slug from ID or use ID directly
        await fetch(`/api/news/${id}/prefetch`, { method: 'POST' });
        return id;
      } catch (error) {
        console.warn(`Failed to prefetch news ${id}:`, error);
        return null;
      }
    });

    const results = await Promise.allSettled(prefetchPromises);
    const successCount = results.filter(r => r.status === 'fulfilled' && r.value).length;
    
    setPrefetchedCount(prev => prev + successCount);
    
    return successCount;
  }, [newsIds, prefetchedCount]);

  return {
    prefetchedCount,
    totalCount: newsIds.length,
    prefetchBatch,
    hasMore: prefetchedCount < newsIds.length
  };
};
